import { CoreEnums } from '@/utils/domain/constants';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import moment from 'moment';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumNamespace } from '@/utils/infrastructure';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import _ from 'lodash';

export const getSourceConfig = (source) => {
  if (source === null || source === undefined) return { value: DEFAULT_EMPTY_VALUE, badge: null };

  const sourceConfig = {
    [CoreEnums.AIConsumptionSource.Assistant]: {
      color: 'default',
      statusText: 'aiConsumptions.text.source.assistant',
    },
    [CoreEnums.AIConsumptionSource.ProcessActionTypeOpenAI]: {
      color: 'default',
      statusText: 'aiConsumptions.text.source.processactiontypeopenai',
    },
    [CoreEnums.AIConsumptionSource.ProcessActionTypeAutomyAI]: {
      color: 'default',
      statusText: 'aiConsumptions.text.source.processactiontypeautomyai',
    },
  };

  return sourceConfig[source] || { value: DEFAULT_EMPTY_VALUE, badge: null };
};

export const getProviderConfig = (provider) => {
  if (provider === null || provider === undefined)
    return { value: DEFAULT_EMPTY_VALUE, badge: null };

  const providerConfig = {
    [CoreEnums.AIProvider.OpenAI]: {
      color: 'default',
      statusText: 'aiConsumptions.text.provider.openai',
    },
  };

  return providerConfig[provider] || { value: DEFAULT_EMPTY_VALUE, badge: null };
};

const YearFilter = {
  type: 'select',
  name: 'year',
  label: 'aiConsumptions.text.year',
  locale: true,
  options: [...Array(10)].map((v, i) => {
    const year = +moment().format('YYYY') - i;
    return { value: year, label: year.toString() };
  }),
  allowClear: true,
};

const normalizeMomentLocale = (loc) =>
  loc ? String(loc).toLowerCase().split('-')[0] : moment.locale();

const buildMonthOptions = (locale) => {
  moment.locale(normalizeMomentLocale(locale));
  return moment.months().map((monthName, index) => ({
    value: index + 1,
    label: _.capitalize(monthName),
  }));
};

const MonthFilter = {
  type: 'select',
  name: 'month',
  label: 'global.text.month',
  locale: true,
  options: buildMonthOptions(),
  allowClear: true,
};

const SourceFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'source',
  label: 'aiConsumptions.text.source',
  enumNamespace: getEnumNamespace(CoreEnums.AIConsumptionSource, 'Pricing'),
  enumSource: CoreEnums.AIConsumptionSource,
  options: createEnumOptions(CoreEnums.AIConsumptionSource, 'aiConsumptions.text.source'),
};

const ModelFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'modelId',
  label: 'aiConsumptions.text.model',
  apiRoute: 'ai/model/paged',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const ChatThreadFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'chatThreadId',
  label: 'aiConsumptions.text.chatThread',
  apiRoute: 'chat/thread/paged',
  params: {
    select: 'id,number',
    orderBy: 'number asc',
  },
  displayField: 'number',
};

const ProcessFilter = {
  ...Filters.ProcessFilter,
};

const ConnectionFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'connectionId',
  label: 'aiConsumptions.text.connection',
  apiRoute: 'connection/paged',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const AppFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'appId',
  label: 'aiConsumptions.text.app',
  apiRoute: 'app/allowed',
  params: {
    select: 'id,name',
    orderBy: 'name asc',
  },
};

const UserFilter = {
  ...Filters.UserFilter,
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

registerModuleFilters('aiConsumption', [SourceFilter]);

// Builds filters with locale-aware month options at call time
export const getFiltersConfig = (locale) => [
  YearFilter,
  { ...MonthFilter, id: `month-${locale || 'default'}`, options: buildMonthOptions(locale) },
  SourceFilter,
  ModelFilter,
  AppFilter,
  ProcessFilter,
  ConnectionFilter,
  UserFilter,
  ChatThreadFilter,
  CreatedDateFilter,
];
